import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaPlus } from 'react-icons/fa';
import ConnectorModal, { KINDS } from './ConnectorModal';
import SchemaBrowser from './SchemaBrowser';
import Btn from '../../components/ui/Btn';
import StatusTag from '../../components/ui/StatusTag';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { toolsService } from '../../services/toolsService';
import { agentsService, friendlyError } from '../../services/agentsService';
import { engineService } from '../../services/engineService';
import { toastSuccess } from '../../utils/toast';

// Integraciones: cómo entran las herramientas a otros sistemas. Un conector guarda la dirección y la clave (en Vault; aquí solo se
// ven los últimos 4). Global = cualquier proyecto lo puede usar; de un proyecto = solo los agentes de ese cliente.
export default function IntegrationsPage() {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [state, setState] = useState('loading');
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);       // null | {} (nuevo) | conector
  const [removing, setRemoving] = useState(null);
  const [tests, setTests] = useState({});
  const [browsing, setBrowsing] = useState(null);     // id del conector cuyas tablas se están viendo

  const load = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([toolsService.listConnectors(), agentsService.listProjects()]);
      setItems(c); setProjects(p); setState('ready');
    } catch (e) { setError(e.code === 'missing_38' ? e.message : friendlyError(e)); setState('error'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const projectName = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p.name])), [projects]);
  const groups = useMemo(() => {
    const g = [{ key: 'global', title: 'Globales', note: 'Cualquier proyecto los puede usar.', items: items.filter((c) => c.project_id === null) }];
    const byProject = {};
    for (const c of items.filter((x) => x.project_id !== null)) (byProject[c.project_id] = byProject[c.project_id] || []).push(c);
    for (const [pid, list] of Object.entries(byProject).sort((a, b) => (projectName[a[0]] || '').localeCompare(projectName[b[0]] || '', 'es'))) {
      g.push({ key: pid, title: projectName[pid] || `Proyecto ${pid}`, note: 'Solo los agentes de este proyecto.', items: list });
    }
    return g.filter((x) => x.items.length);
  }, [items, projectName]);

  const test = async (c) => {
    setTests((t) => ({ ...t, [c.id]: { busy: true } }));
    try { const r = await engineService.testConnector(c.id); setTests((t) => ({ ...t, [c.id]: r })); }
    catch (e) { setTests((t) => ({ ...t, [c.id]: { ok: false, message: e.message } })); }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-[34px] sm:text-[40px] leading-[1.05] font-bold text-fg">Integraciones</h1>
          <p className="mt-2 text-[15px] text-fg-dim max-w-[60ch]">Las conexiones con otros sistemas que usan las herramientas de los agentes: una base Supabase o cualquier API por https. Las claves se guardan cifradas.</p>
        </div>
        <Btn variant="primary" icon={FaPlus} onClick={() => setEditing({})}>Agregar conector</Btn>
      </header>

      {state === 'loading' && <div aria-busy="true" className="space-y-3">{[0, 1].map((i) => <div key={i} className="h-20 skeleton rounded-2xl" />)}</div>}
      {state === 'error' && (
        <div role="alert" className="rounded-xl border border-signal/30 bg-signal-soft p-5 text-sm">
          <p className="text-fg font-medium">No se pudieron cargar los conectores.</p><p className="mt-1 text-fg-dim">{error}</p>
          <Btn size="sm" className="mt-3" onClick={() => { setState('loading'); load(); }}>Reintentar</Btn>
        </div>
      )}
      {state === 'ready' && !items.length && (
        <div className="rounded-2xl border border-dashed border-fg/15 p-8 text-center">
          <p className="text-fg font-medium">Todavía no hay conectores.</p>
          <p className="mt-1 text-[14px] text-fg-dim max-w-[52ch] mx-auto">Agrega el Supabase de un cliente o una API para que sus herramientas puedan guardar leads, consultar pedidos o avisar a otro sistema.</p>
          <Btn variant="primary" className="mt-4" icon={FaPlus} onClick={() => setEditing({})}>Agregar conector</Btn>
        </div>
      )}

      <div className="space-y-8">
        {groups.map((g) => (
          <section key={g.key} aria-labelledby={`g-${g.key}`}>
            <div className="flex items-baseline gap-3 mb-3">
              <h2 id={`g-${g.key}`} className="text-[15px] font-semibold text-fg">{g.title}</h2>
              <span className="text-[12.5px] text-fg-faint">{g.note}</span>
            </div>
            <ul className="rounded-2xl bg-panel ring-1 ring-line divide-y divide-line">
              {g.items.map((c) => {
                const K = KINDS[c.kind] || KINDS.http;
                const t = tests[c.id];
                return (
                  <li key={c.id} className="px-5 py-4">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                      <span className="grid place-items-center h-9 w-9 rounded-xl bg-raised text-fg-dim shrink-0"><K.icon aria-hidden /></span>
                      <div className="min-w-0 flex-1">
                        <p className="flex flex-wrap items-center gap-2 text-[14.5px] font-semibold text-fg">
                          {c.name}
                          {c.status === 'error' ? <StatusTag tone="caution">Con error</StatusTag> : null}
                        </p>
                        <p className="text-[12.5px] text-fg-dim truncate">
                          {K.label} · <span className="font-mono">{c.kind === 'supabase' ? c.config?.url : c.kind === 'gsheets' ? c.config?.client_email : c.config?.base_url}</span>
                          {c.kind === 'gsheets' ? ' · comparte cada hoja con ese correo' : c.last4 ? <> · clave …<span className="font-mono">{c.last4}</span></> : ' · sin clave'}
                        </p>
                      </div>
                      <div className="flex gap-1.5">
                        {c.kind === 'supabase' && <Btn size="sm" variant="ghost" aria-expanded={browsing === c.id} onClick={() => setBrowsing(browsing === c.id ? null : c.id)}>{browsing === c.id ? 'Ocultar tablas' : 'Ver tablas'}</Btn>}
                        <Btn size="sm" onClick={() => test(c)} loading={t?.busy}>Probar</Btn>
                        <Btn size="sm" variant="ghost" onClick={() => setEditing(c)}>Editar</Btn>
                        <Btn size="sm" variant="ghost" onClick={() => setRemoving(c)}>Borrar</Btn>
                      </div>
                    </div>
                    {c.status === 'error' && c.last_error && <p className="mt-2 text-[12.5px] text-caution">Último error: {c.last_error}</p>}
                    {t && !t.busy && <p role="status" className={`mt-2 text-[13px] ${t.ok ? 'text-live' : 'text-signal'}`}>{t.message}</p>}
                    {browsing === c.id && c.kind === 'supabase' && <SchemaBrowser connectorId={c.id} />}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>

      <ConnectorModal open={editing !== null} connector={editing} projects={projects} onClose={() => setEditing(null)}
        onSaved={async (msg, r) => { const wasNew = !editing?.id; setEditing(null); toastSuccess(msg); await load(); if (wasNew && r?.id) { test({ id: r.id }); setBrowsing(r.id); } }} />
      <ConfirmModal open={Boolean(removing)} onClose={() => setRemoving(null)} title={`Borrar «${removing?.name}»`} confirmLabel="Borrar conector" tone="danger"
        onConfirm={async () => { try { await toolsService.deleteConnector(removing.id); toastSuccess('Conector borrado'); await load(); } catch (e) { throw new Error(friendlyError(e)); } }}>
        <p>Se borra también su clave. Si alguna herramienta o agente lo usa, no se borra y te dice dónde quitarlo primero.</p>
      </ConfirmModal>
    </div>
  );
}
